import { useEffect, useState, useCallback } from 'react';
import { payrollAIAPI, employeeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Sparkles, Calculator, Check, X, Lock, Edit, 
  Brain, AlertTriangle, CheckCircle2, Loader2
} from 'lucide-react';

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 3 }, (_, i) => currentYear - 1 + i);

const statusConfig = {
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  locked: { label: 'Locked', color: 'bg-green-100 text-green-800', icon: Lock },
};

export default function PayrollAIPage() {
  const { user: _user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [computations, setComputations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    tds: '', pf_employee: '', professional_tax: '', override_reason: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, compRes] = await Promise.all([
        employeeAPI.getAll({ status: 'active' }),
        payrollAIAPI.listComputations({ year: selectedYear })
      ]);
      setEmployees(empRes.data);
      setComputations(compRes.data);
    } catch (_error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCompute = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    setComputing(true);
    try {
      const res = await payrollAIAPI.compute(selectedEmployee, {
        month: selectedMonth,
        year: selectedYear
      });
      toast.success('AI computation completed!');
      setSelectedComputation(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Computation failed');
    } finally {
      setComputing(false);
    }
  };

  const handleAccept = async (computationId) => {
    try {
      await payrollAIAPI.accept(computationId);
      toast.success('AI suggestion accepted');
      fetchData();
      setSelectedComputation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleOverride = async () => {
    if (!selectedComputation || !overrideForm.override_reason) {
      toast.error('Please provide override reason');
      return;
    }
    try {
      const data = {
        override_reason: overrideForm.override_reason
      };
      if (overrideForm.tds) data.tds = parseFloat(overrideForm.tds);
      if (overrideForm.pf_employee) data.pf_employee = parseFloat(overrideForm.pf_employee);
      if (overrideForm.professional_tax) data.professional_tax = parseFloat(overrideForm.professional_tax);
      
      await payrollAIAPI.override(selectedComputation.id, data);
      toast.success('Values overridden successfully');
      setOverrideDialogOpen(false);
      setOverrideForm({ tds: '', pf_employee: '', professional_tax: '', override_reason: '' });
      fetchData();
      setSelectedComputation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Override failed');
    }
  };

  const handleLock = async (computationId) => {
    if (!confirm('Are you sure? Locked payroll cannot be modified.')) return;
    try {
      await payrollAIAPI.lock(computationId);
      toast.success('Payroll locked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock');
    }
  };

  const viewComputation = async (comp) => {
    try {
      const res = await payrollAIAPI.getSuggestion(comp.emp_id, comp.month, comp.year);
      setSelectedComputation(res.data);
    } catch (error) {
      toast.error('Failed to load computation details');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-r from-primary/20 to-accent/20">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Payroll AI</h1>
            <p className="text-muted-foreground">AI-powered Indian payroll computation with Gemini</p>
          </div>
        </div>
      </div>

      {/* Computation Panel */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Run AI Computation
          </CardTitle>
          <CardDescription>Select employee and period to compute TDS and deductions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-48">
              <Label>Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label>Month</Label>
              <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-24">
              <Label>Year</Label>
              <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCompute} disabled={computing} className="gap-2">
              {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {computing ? 'Computing...' : 'Compute with AI'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Result Panel */}
      {selectedComputation && (
        <Card className="border-2 border-primary/30 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI Computation Result
                </CardTitle>
                <CardDescription>
                  {selectedComputation.employee_name} • {months.find(m => m.value === selectedComputation.month)?.label} {selectedComputation.year}
                </CardDescription>
              </div>
              <Badge className={statusConfig[selectedComputation.status]?.color}>
                {statusConfig[selectedComputation.status]?.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <Tabs defaultValue="suggestion">
              <TabsList className="mb-4">
                <TabsTrigger value="suggestion">AI Suggestion</TabsTrigger>
                {selectedComputation.final_values && <TabsTrigger value="final">Final Values</TabsTrigger>}
                <TabsTrigger value="comparison">Regime Comparison</TabsTrigger>
              </TabsList>

              <TabsContent value="suggestion">
                {selectedComputation.gemini_suggestion && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600 mb-1">Gross Salary</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(selectedComputation.gemini_suggestion.gross_salary)}
                        </p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 mb-1">Suggested TDS</p>
                        <p className="text-xl font-bold text-red-700">
                          {formatCurrency(selectedComputation.gemini_suggestion.deductions?.tds)}
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <p className="text-xs text-orange-600 mb-1">PF Deduction</p>
                        <p className="text-xl font-bold text-orange-700">
                          {formatCurrency(selectedComputation.gemini_suggestion.deductions?.pf_employee)}
                        </p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-lg">
                        <p className="text-xs text-primary mb-1">Net Salary</p>
                        <p className="text-xl font-bold text-primary">
                          {formatCurrency(selectedComputation.gemini_suggestion.net_salary)}
                        </p>
                      </div>
                    </div>

                    {/* Tax Computation Details */}
                    {selectedComputation.gemini_suggestion.computation && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3">Tax Computation (FY {selectedComputation.fiscal_year})</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Annual Gross</p>
                            <p className="font-medium">{formatCurrency(selectedComputation.gemini_suggestion.computation.annual_gross)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Taxable Income</p>
                            <p className="font-medium">{formatCurrency(selectedComputation.gemini_suggestion.computation.taxable_income)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total Tax (incl. Cess)</p>
                            <p className="font-medium">{formatCurrency(selectedComputation.gemini_suggestion.computation.total_tax)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">TDS Already Paid</p>
                            <p className="font-medium">{formatCurrency(selectedComputation.gemini_suggestion.computation.tds_already_paid)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Monthly TDS Suggested</p>
                            <p className="font-medium text-primary">{formatCurrency(selectedComputation.gemini_suggestion.computation.monthly_tds_suggested)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Effective Tax Rate</p>
                            <p className="font-medium">{selectedComputation.gemini_suggestion.computation.effective_rate_pct?.toFixed(2)}%</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Assumptions */}
                    {selectedComputation.gemini_suggestion.assumptions?.length > 0 && (
                      <div className="bg-muted/50 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          AI Assumptions
                        </h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {selectedComputation.gemini_suggestion.assumptions.map((a, i) => (
                            <li key={i}>• {a}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {selectedComputation.status === 'pending_review' && (
                      <div className="flex gap-3 pt-4 border-t">
                        <Button onClick={() => handleAccept(selectedComputation.id)} className="gap-2">
                          <Check className="w-4 h-4" /> Accept AI Suggestion
                        </Button>
                        <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="gap-2">
                              <Edit className="w-4 h-4" /> Override Values
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Override AI Values</DialogTitle>
                              <DialogDescription>Modify the AI-suggested values with your adjustments</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label>TDS Amount</Label>
                                  <Input
                                    type="number"
                                    placeholder={selectedComputation.gemini_suggestion.deductions?.tds?.toString()}
                                    value={overrideForm.tds}
                                    onChange={e => setOverrideForm({ ...overrideForm, tds: e.target.value })}
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">AI: {formatCurrency(selectedComputation.gemini_suggestion.deductions?.tds)}</p>
                                </div>
                                <div>
                                  <Label>PF (Employee)</Label>
                                  <Input
                                    type="number"
                                    placeholder={selectedComputation.gemini_suggestion.deductions?.pf_employee?.toString()}
                                    value={overrideForm.pf_employee}
                                    onChange={e => setOverrideForm({ ...overrideForm, pf_employee: e.target.value })}
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">AI: {formatCurrency(selectedComputation.gemini_suggestion.deductions?.pf_employee)}</p>
                                </div>
                              </div>
                              <div>
                                <Label>Professional Tax</Label>
                                <Input
                                  type="number"
                                  placeholder={selectedComputation.gemini_suggestion.deductions?.professional_tax?.toString()}
                                  value={overrideForm.professional_tax}
                                  onChange={e => setOverrideForm({ ...overrideForm, professional_tax: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label>Override Reason *</Label>
                                <Textarea
                                  placeholder="Why are you overriding the AI suggestion?"
                                  value={overrideForm.override_reason}
                                  onChange={e => setOverrideForm({ ...overrideForm, override_reason: e.target.value })}
                                  required
                                />
                              </div>
                              <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
                                <Button onClick={handleOverride}>Save Override</Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button variant="ghost" onClick={() => setSelectedComputation(null)}>
                          <X className="w-4 h-4" /> Close
                        </Button>
                      </div>
                    )}

                    {selectedComputation.status === 'reviewed' && (
                      <div className="flex gap-3 pt-4 border-t">
                        <Button onClick={() => handleLock(selectedComputation.id)} className="gap-2 bg-green-600 hover:bg-green-700">
                          <Lock className="w-4 h-4" /> Lock Payroll
                        </Button>
                        <Button variant="ghost" onClick={() => setSelectedComputation(null)}>Close</Button>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="final">
                {selectedComputation.final_values && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-600 mb-1">Final Gross</p>
                        <p className="text-xl font-bold text-green-700">{formatCurrency(selectedComputation.final_values.gross_salary)}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <p className="text-xs text-red-600 mb-1">Final TDS</p>
                        <p className="text-xl font-bold text-red-700">{formatCurrency(selectedComputation.final_values.tds)}</p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <p className="text-xs text-orange-600 mb-1">Total Deductions</p>
                        <p className="text-xl font-bold text-orange-700">{formatCurrency(selectedComputation.final_values.total_deductions)}</p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-lg">
                        <p className="text-xs text-primary mb-1">Final Net</p>
                        <p className="text-xl font-bold text-primary">{formatCurrency(selectedComputation.final_values.net_salary)}</p>
                      </div>
                    </div>
                    {selectedComputation.final_values.override_reason && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="font-medium text-amber-800 mb-1">Override Reason:</p>
                        <p className="text-sm text-amber-700">{selectedComputation.final_values.override_reason}</p>
                        {selectedComputation.final_values.modified_fields?.length > 0 && (
                          <p className="text-xs text-amber-600 mt-2">
                            Modified fields: {selectedComputation.final_values.modified_fields.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="comparison">
                {selectedComputation.gemini_suggestion?.regime_comparison && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-6 rounded-lg border-2 ${selectedComputation.gemini_suggestion.regime_comparison.recommended === 'new' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">New Regime</h4>
                          {selectedComputation.gemini_suggestion.regime_comparison.recommended === 'new' && (
                            <Badge className="bg-green-500">Recommended</Badge>
                          )}
                        </div>
                        <p className="text-2xl font-bold">
                          {formatCurrency(selectedComputation.gemini_suggestion.regime_comparison.new_regime_tax)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">No deductions, lower slab rates</p>
                      </div>
                      <div className={`p-6 rounded-lg border-2 ${selectedComputation.gemini_suggestion.regime_comparison.recommended === 'old' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">Old Regime</h4>
                          {selectedComputation.gemini_suggestion.regime_comparison.recommended === 'old' && (
                            <Badge className="bg-green-500">Recommended</Badge>
                          )}
                        </div>
                        <p className="text-2xl font-bold">
                          {formatCurrency(selectedComputation.gemini_suggestion.regime_comparison.old_regime_tax)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">80C, 80D deductions applicable</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Potential Savings:</strong> {formatCurrency(Math.abs(
                          selectedComputation.gemini_suggestion.regime_comparison.old_regime_tax - 
                          selectedComputation.gemini_suggestion.regime_comparison.new_regime_tax
                        ))} by choosing {selectedComputation.gemini_suggestion.regime_comparison.recommended} regime
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Computations List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Computations</CardTitle>
          <CardDescription>All AI-computed payroll records for {selectedYear}</CardDescription>
        </CardHeader>
        <CardContent>
          {computations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No computations yet. Run your first AI computation above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">TDS</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {computations.map(comp => {
                  const values = comp.final_values || comp.gemini_suggestion || {};
                  const StatusIcon = statusConfig[comp.status]?.icon || AlertTriangle;
                  return (
                    <TableRow key={comp.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{comp.employee_name}</p>
                          <p className="text-xs text-muted-foreground">{comp.employee_id_display}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {months.find(m => m.value === comp.month)?.label} {comp.year}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(values.gross_salary)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(values.deductions?.tds || values.tds)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(values.net_salary)}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig[comp.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[comp.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => viewComputation(comp)}>
                          View
                        </Button>
                        {comp.status === 'reviewed' && (
                          <Button variant="ghost" size="sm" onClick={() => handleLock(comp.id)}>
                            <Lock className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
